import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  User, 
  Lock, 
  Eye, 
  EyeOff, 
  Loader2,
  Heart,
  Activity,
  Pill,
  Syringe,
  HeartPulse,
  Cross,
  Thermometer,
  Microscope
} from 'lucide-react';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(username, password);
      
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'teacher') {
        navigate('/teacher');
      } else {
        navigate('/student');
      }
    } catch (err) {
      setError(err.response?.data?.non_field_errors?.[0] || 'Login yoki parol noto\'g\'ri');
    } finally {
      setLoading(false);
    }
  };

  // Floating tibbiy ikonkalar
  const floatingIcons = [
    { Icon: Heart, size: 40 },
    { Icon: Activity, size: 35 },
    { Icon: Pill, size: 32 },
    { Icon: Syringe, size: 38 },
    { Icon: HeartPulse, size: 45 },
    { Icon: Cross, size: 30 },
    { Icon: Thermometer, size: 36 },
    { Icon: Microscope, size: 42 },
  ];

  return (
    <div className="login-container">
      {/* Floating Medical Icons */}
      <div className="floating-icons">
        {floatingIcons.map((item, index) => (
          <div key={index} className="floating-icon">
            <item.Icon size={item.size} color="white" />
          </div>
        ))}
      </div>

      {/* DNA Helix Animation */}
      <div className="dna-helix">
        <div className="dna-strand">
          {[...Array(14)].map((_, i) => (
            <div key={i} className="dna-dot" />
          ))}
        </div>
      </div>

      {/* Heartbeat Line */}
      <div className="heartbeat-line">
        <svg viewBox="0 0 1200 80" preserveAspectRatio="none">
          <path 
            className="heartbeat-path"
            d="M0,40 L200,40 L230,40 L250,10 L270,70 L290,20 L310,60 L330,40 L400,40 L430,40 L450,5 L470,75 L490,15 L510,65 L530,40 L600,40 L630,40 L650,10 L670,70 L690,20 L710,60 L730,40 L800,40 L830,40 L850,5 L870,75 L890,15 L910,65 L930,40 L1000,40 L1030,40 L1050,10 L1070,70 L1090,20 L1110,60 L1130,40 L1200,40"
          />
        </svg>
      </div>

      {/* Login Card */}
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo" style={{ marginTop: '100px' }}>
            <img src="/logo.png" alt="BXTT Logo" />
          </div>
          <h1>Buxoro Xalqaro Tibbiyot Texnikumi</h1>
          <p>Ta'lim faoliyati va ta'lim jarayonlarini boshqarish elektron platformasi</p>
          <div className="login-subtitle">
            <HeartPulse size={16} />
            <span>Bilim — sog'lom kelajak kaliti</span>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Login</label>
            <div className="input-wrapper">
              <User className="input-icon" size={20} />
              <input
                type="text"
                className="form-input"
                placeholder="Loginni kiriting"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Parol</label>
            <div className="input-wrapper">
              <Lock className="input-icon" size={20} />
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="Parolni kiriting"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="spinner" size={20} />
                Kirish...
              </>
            ) : (
              <>
                <Activity size={20} />
                Tizimga kirish
              </>
            )}
          </button>
        </form>

        <div style={{ 
          marginTop: '24px',
          paddingTop: '20px',
          borderTop: '1px solid rgba(255,255,255,0.2)'
        }}>
          <p style={{ 
            color: 'rgba(255,255,255,0.7)', 
            fontSize: '13px', 
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            Foydali havolalar
          </p>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '16px'
          }}>
            <a 
              href="https://bxtt.uz" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: 'white',
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #0891b2, #0e7490)',
                borderRadius: '10px',
                transition: 'all 0.3s',
                boxShadow: '0 4px 15px rgba(8, 145, 178, 0.3)'
              }}
            >
              🌐 bxtt.uz
            </a>
            <a 
              href="https://prof-emis.edu.uz" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: 'white',
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #0891b2, #0e7490)',
                borderRadius: '10px',
                transition: 'all 0.3s',
                boxShadow: '0 4px 15px rgba(8, 145, 178, 0.3)'
              }}
            >
              🎓 prof-emis.edu.uz
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
